import { Features } from '@alsid/common'
import {
  IconAttackPath,
  IconDashboard,
  IconIdentities,
  IconIoA,
  IconIoeAd,
  IconIoeEntraId,
  IconTopology,
  IconTrailflow
} from '@app/components/Icon/MainMenu'
import IconAttackPathAnalysis from '@app/components/Icon/MainMenu/IconAttackPathAnalysis'
import IconIdentityExplorer from '@app/components/Icon/MainMenu/IconIdentityExplorer'
import IconLandingPage from '@app/components/Icon/MainMenu/IconLandingPage'
import IconWeaknesses from '@app/components/Icon/MainMenu/IconWeaknesses'
import { canAccessToAttackPath } from '@app/pages/AttackPath/permissions'
import { canAccessToDashbards } from '@app/pages/Dashboard/permissions'
import { canAccessToSecurityEngine } from '@app/pages/ExposureCenter/permissions'
import {
  canAccessToApa,
  canAccessToIdentities
} from '@app/pages/Identities/permissions'
import { canAccessToIdentityExplorer } from '@app/pages/IdentityExplorer/permissions'
import { canAccessToIoABoard } from '@app/pages/IoA/IoABoardPage/permissions'
import {
  canAccessToAdIoE,
  canAccessToIdentityIoE
} from '@app/pages/IoE/IoEBoardPage/permissions'
import { IoEBoardMenuItemKey } from '@app/pages/IoE/IoEBoardPage/types'
import { canAccessToLandingPage } from '@app/pages/LandingPage/permissions'
import type { IMainMenuEntry } from '@app/pages/MainMenu/types'
import { MainMenuKey } from '@app/pages/MainMenu/types'
import { canAccessToTopology } from '@app/pages/Topology/permissions'
import { canAccessToTrailFlow } from '@app/pages/TrailFlow/permissions'
import { canAccessToWeaknesses } from '@app/pages/Weaknesses/permissions'
import { AppRouteName } from '@app/routes'
import { filterFalsies } from '@libs/filterFalsies'
import { flatMap } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import StoreMenu from './helpers/StoreMenu'
import { MenuEntryType } from './helpers/StoreMenu/types'
import StoreModal from './helpers/StoreModal'
import StoreBase from './StoreBase'
import type StoreRoot from './StoreRoot'
import type { IStoreOptions } from './types'

export default class StoreLayout extends StoreBase {
  public storeModalChangesNotSaved = new StoreModal(this.storeRoot)

  public storeMenuMainNavigation = new StoreMenu<IMainMenuEntry>(
    this.storeRoot,
    {}
  )

  public translate = this.storeRoot.appTranslator.bindOptions({
    namespaces: ['Layout.MainMenu']
  })

  private $currentPathname = observable.box<string>('')

  constructor(storeRoot: StoreRoot, options: IStoreOptions = {}) {
    super(storeRoot, options)

    makeObservable(this)
  }

  /**
   * Initialize entries of the main navigation.
   */
  initializeMainNavigation(): this {
    const { storeDebug, storeRbac } = this.storeRoot.stores

    const apaAnalysisUrl = `${this.storeRoot.environment.tenablePlatformOrigin()}/t1/apa/dashboard`

    const isDevFlag = storeDebug.isDevFlag()

    const exposureSubMenuEntries = this._getExposureSubMenuEntries()
    const inventoriesSubMenuEntries = this._getInventoriesSubMenuEntries()

    const mainMenuEntries: Map<IMainMenuEntry, IMainMenuEntry[]> = new Map([
      [
        {
          key: MainMenuKey.identity360Title,
          type: MenuEntryType.category,
          label: this.translate('Identity Security'),
          labelledBy: 'Identity Security'
        },
        filterFalsies<IMainMenuEntry>([
          storeRbac.isUserGrantedTo(canAccessToLandingPage) && {
            key: MainMenuKey.landingPage,
            type: MenuEntryType.link,
            label: this.translate('Insights'),
            labelledBy: 'LandingPage',
            routeDefinition: {
              routeName: AppRouteName.Insights,
              parameters: {}
            },
            icon: IconLandingPage,
            isCloudOnly: !storeRbac.isUserGrantedAccordingFeatureFlag(
              Features.KAPTEYN_INSIGHTS_DISCOVERY
            )
          },
          storeRbac.isUserGrantedTo(canAccessToIdentityExplorer) && {
            key: MainMenuKey.identityExplorer,
            type: MenuEntryType.link,
            label: this.translate('Identity Explorer'),
            labelledBy: 'Identity Explorer',
            routeDefinition: {
              routeName: AppRouteName.IdentityExplorer,
              parameters: {}
            },
            icon: IconIdentityExplorer
          },
          storeRbac.isUserGrantedTo(canAccessToIdentities) &&
          storeRbac.isUserGrantedAccordingFeatureFlag(
            Features.ID360_IDENTITIES_ACCOUNTS
          )
            ? {
                key: MainMenuKey.inventories,
                type: MenuEntryType.expandable,
                label: this.translate('Identity 360'),
                labelledBy: 'Identities',
                subItems: inventoriesSubMenuEntries,
                icon: IconIdentities
              }
            : storeRbac.isUserGrantedTo(canAccessToIdentities)
              ? {
                  key: MainMenuKey.identities,
                  type: MenuEntryType.link,
                  label: this.translate('Identity 360'),
                  labelledBy: 'Identities',
                  routeDefinition: {
                    routeName: AppRouteName.Identities,
                    parameters: {}
                  },
                  icon: IconIdentities
                }
              : undefined,
          storeRbac.isUserGrantedTo(canAccessToSecurityEngine)
            ? storeRbac.isUserGrantedAccordingFeatureFlag(
                Features.EXPOSURE_CENTER_REDESIGN
              )
              ? {
                  key: MainMenuKey.exposures,
                  type: MenuEntryType.link,
                  label: this.translate('Exposure Center'),
                  labelledBy: 'Exposure Center',
                  routeDefinition: {
                    routeName: AppRouteName.ExposureCenter,
                    parameters: {}
                  },
                  icon: IconWeaknesses
                }
              : {
                  key: MainMenuKey.exposures,
                  type: MenuEntryType.expandable,
                  label: this.translate('Exposure Center'),
                  labelledBy: 'Exposure Center',
                  subItems: exposureSubMenuEntries,
                  icon: IconWeaknesses
                }
            : undefined,
          storeRbac.isUserGrantedTo(canAccessToApa) && {
            key: MainMenuKey.attackPathAnalysis,
            type: MenuEntryType.externalLink,
            label: this.translate('Attack Path Analysis'),
            labelledBy: 'Attack path analysis',
            href: apaAnalysisUrl,
            icon: IconAttackPathAnalysis
          }
        ])
      ],
      [
        {
          key: MainMenuKey.activeDirectoryTitle,
          type: MenuEntryType.category,
          label: this.translate('Active Directory'),
          labelledBy: 'ActiveDirectory'
        },
        filterFalsies<IMainMenuEntry>([
          storeRbac.isUserGrantedTo(canAccessToDashbards) && {
            key: MainMenuKey.dashboard,
            type: MenuEntryType.link,
            label: this.translate('Dashboards'),
            labelledBy: 'Dashboards',
            routeDefinition: {
              routeName: AppRouteName.Dashboard,
              parameters: {}
            },
            icon: IconDashboard
          },
          storeRbac.isUserGrantedTo(canAccessToTrailFlow) && {
            key: MainMenuKey.trailflow,
            type: MenuEntryType.link,
            label: this.translate('Trail Flow'),
            labelledBy: 'Trail Flow',
            routeDefinition: {
              routeName: AppRouteName.TrailFlow,
              parameters: {}
            },
            icon: IconTrailflow
          },
          storeRbac.isUserGrantedTo(canAccessToAdIoE) && {
            key: IoEBoardMenuItemKey.ActiveDirectory,
            type: MenuEntryType.link,
            label: this.translate('Indicators of Exposure (AD)'),
            labelledBy: 'Indicators of Exposure (AD)',
            routeDefinition: {
              routeName: AppRouteName.IoE_Board_AD,
              parameters: {}
            },
            icon: IconIoeAd
          },
          storeRbac.isUserGrantedTo(canAccessToIoABoard) && {
            key: MainMenuKey.ioa,
            type: MenuEntryType.link,
            label: this.translate('Indicators of Attack'),
            labelledBy: 'Indicators of Attack',
            routeDefinition: {
              routeName: AppRouteName.IoA_Board,
              parameters: {}
            },
            icon: IconIoA
          },
          storeRbac.isUserGrantedTo(canAccessToTopology) && {
            key: MainMenuKey.topology,
            type: MenuEntryType.link,
            label: this.translate('Topology'),
            labelledBy: 'Topology',
            routeDefinition: {
              routeName: AppRouteName.Topology,
              parameters: {}
            },
            icon: IconTopology
          },
          storeRbac.isUserGrantedTo(canAccessToAttackPath) && {
            key: MainMenuKey.attackPath,
            type: MenuEntryType.link,
            label: this.translate('AD Attack Path'),
            labelledBy: 'AD Attack Path',
            routeDefinition: {
              routeName: AppRouteName.AttackPath,
              parameters: {}
            },
            icon: IconAttackPath
          }
        ])
      ],
      [
        {
          key: MainMenuKey.entraIdTitle,
          type: MenuEntryType.category,
          label: this.translate('Entra ID'),
          labelledBy: 'EntraId'
        },
        filterFalsies<IMainMenuEntry>([
          storeRbac.isUserGrantedTo(canAccessToIdentityIoE) && {
            key: IoEBoardMenuItemKey.AzureAD,
            type: MenuEntryType.link,
            label: this.translate('Indicators of Exposure (Entra ID)'),
            labelledBy: 'Indicators of Exposure (Entra ID)',
            routeDefinition: {
              routeName: AppRouteName.IoE_Board_MEID,
              parameters: {}
            },
            icon: IconIoeEntraId
          }
        ])
      ]
    ])

    if (isDevFlag) {
      mainMenuEntries.set(
        {
          key: MainMenuKey.debugTitle,
          type: MenuEntryType.category,
          label: this.translate('Debug'),
          labelledBy: 'Debug',
          icon: IconDashboard
        },
        [
          {
            key: MainMenuKey.apiInspector,
            type: MenuEntryType.link,
            label: 'API inspector',
            labelledBy: 'API Inspector',
            routeDefinition: {
              routeName: AppRouteName.Debug_APIInspector,
              parameters: {}
            },
            icon: IconDashboard
          }
        ]
      )
    }

    const mainMenuEntriesAsArray = flatMap(
      Array.from(mainMenuEntries.entries()).map(([titleEntry, menuEntries]) => {
        // don't display title if there is no menu entry
        if (!menuEntries.length) {
          return menuEntries
        }

        return [titleEntry, ...menuEntries]
      })
    )

    this.storeMenuMainNavigation.setMenuEntries(mainMenuEntriesAsArray)

    return this
  }

  /**
   * Return the product name according various parameters.
   */
  getProductName(): string {
    return 'Tenable Identity Exposure' // (also named Tenable.IE sometimes)
  }

  /**
   * Return the product name related to the Authentication.
   */
  getAuthenticationProductName(): string {
    const { storeAuthentication } = this.storeRoot.stores

    return storeAuthentication.isToneUser()
      ? 'Tenable.one'
      : this.getProductName()
  }

  /**
   * Action
   */
  @action
  setCurrentPathname(pathname: string): void {
    this.$currentPathname.set(pathname)
  }

  /**
   * Computed
   */

  @computed
  get currentPathname(): string {
    return this.$currentPathname.get()
  }

  /**
   * Return true if TUL should be displayed according various parameters.
   *
   * This method is computed in order to observe `storeAuthentication.isAuthenticated` and
   * update the CSS that hides TUL on the Login page (https://tenable.atlassian.net/browse/AD-24392).
   */
  @computed
  get isTulDisplayed(): boolean {
    const { storeAuthentication } = this.storeRoot.stores
    const { appRouter } = this.storeRoot

    return (
      storeAuthentication.isAuthenticated &&
      !document.location.href.includes('/pdf/') &&
      !appRouter.isPathnameIsStartingByOneOf(this.currentPathname, [
        '/pdf',
        '/reports',
        '/auth/license',
        '/auth/eula',
        '/auth/release-notes'
      ])
    )
  }

  /**
   * Define if the ribbon for configuration message should be displayed.
   * If license ok: yes
   * Otherwise: no
   */
  @computed
  get isRibbonMessageDisplayed(): boolean {
    const { storeAuthentication, storeLicense } = this.storeRoot.stores

    if (!storeAuthentication.isAuthenticated) {
      return false
    }

    if (!storeLicense.isRibbonVisible) {
      return false
    }

    if (storeLicense.isLicenseExpired) {
      return false
    }

    if (!this.storeRoot.environment.config.app.settings.ribbon) {
      return false
    }

    // finally display the message only if no ribbon about the license is displayed
    return !this.isRibbonLicenseDisplayed
  }

  /**
   * Define if the ribbon for license information should be displayed.
   * Onprem: yes
   * Onsaas: no, information about license violations are done with Pendo.
   */
  @computed
  get isRibbonLicenseDisplayed(): boolean {
    const { storeAuthentication, storeAbout, storeLicense } =
      this.storeRoot.stores

    // don't use build-in ribbon on saas, information is provided by Pendo
    if (storeAbout.isSaaSDeployment === null || storeAbout.isSaaSDeployment) {
      return false
    }

    if (!storeAuthentication.isAuthenticated) {
      return false
    }

    if (!storeLicense.isRibbonVisible) {
      return false
    }

    // TODO - Should we fallback on built-in ribbon if Pendo is unavailable?

    return true
  }

  /**
   * Private
   */

  private _getInventoriesSubMenuEntries(): IMainMenuEntry[] {
    return [
      {
        key: MainMenuKey.identities,
        type: MenuEntryType.link,
        label: this.translate('Identities'),
        labelledBy: 'Identities',
        href: '/inventories?globalinventory=~inventories%3FtabOrigin%3Didentities',
        onClick: () =>
          this.storeRoot.appRouter.history.push(
            '/inventories?globalinventory=~inventories%3FtabOrigin%3Didentities'
          )
      },
      {
        key: MainMenuKey.accounts,
        type: MenuEntryType.link,
        label: this.translate('Accounts'),
        labelledBy: 'Accounts',
        href: '/inventories?globalinventory=~inventories%3FtabOrigin%3Daccounts',
        onClick: () =>
          this.storeRoot.appRouter.history.push(
            '/inventories?globalinventory=~inventories%3FtabOrigin%3Daccounts'
          )
      }
    ]
  }

  private _getExposureSubMenuEntries(): IMainMenuEntry[] {
    const { storeRbac } = this.storeRoot.stores

    return [
      ...(storeRbac.isUserGrantedTo(canAccessToWeaknesses)
        ? [
            {
              key: MainMenuKey.weaknesses,
              type: MenuEntryType.link,
              label: this.translate('Exposure Overview'),
              labelledBy: 'Exposure Overview',
              routeDefinition: {
                routeName: AppRouteName.ExposureOverview,
                parameters: {}
              }
            }
          ]
        : ([] as any[])),
      ...(storeRbac.isUserGrantedTo(canAccessToSecurityEngine)
        ? [
            {
              key: MainMenuKey.exposureInstances,
              type: MenuEntryType.link,
              label: this.translate('Exposure Instances'),
              labelledBy: 'Exposure Instances',
              routeDefinition: {
                routeName: AppRouteName.ExposureInstances,
                parameters: {}
              }
            }
          ]
        : [])
    ]
  }
}
